import { jsx, jsxs } from "react/jsx-runtime";
import { css } from "@/styled-system/css";
import { FormField, Box, Slider, Label, Tooltip, Paragraph } from "@zillow/constellation-10";
import { SliderRootContext } from "@zillow/constellation-10/components/slider/context/slider-root.context";
import React, { useState, useEffect, useMemo, useContext, useCallback } from "react";
import { currencyFormatter } from "../../RentVsBuyCalculator.mixins.esm.js";
import { gridWithMargin, sliderWrapper, sliderValueLabel, gridWithPadding, gridEnd, sliderLabel, formFieldWrapper, sliderHandleStyles } from "./CustomSlider.styles.esm.js";
const PAGE_KEYS = ["PageUp", "PageDown"];
const END_KEYS = ["Home", "End"];
const ALL_KEYS = /* @__PURE__ */ new Set([...PAGE_KEYS, "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", ...END_KEYS]);
const DIRECTION_KEYS = {
  increase: ["PageUp", "ArrowUp", "ArrowRight"],
  decrease: ["PageDown", "ArrowDown", "ArrowLeft"]
};
const NoTooltipSliderHandle = ({
  ariaLabel,
  ariaValuetext
}) => {
  const {
    value,
    min,
    max,
    handles,
    setValue,
    step,
    disabled,
    shiftStep,
    reverse
  } = useContext(SliderRootContext);
  const [handle, setHandle] = useState();
  const setHandleRef = useCallback((node) => {
    if (node && !handles.has(node)) {
      handles.add(node);
      setHandle(node);
    }
  }, [handles]);
  const handleIndex = useMemo(() => [...handles].findIndex((el) => el === handle), [handle, handles]);
  const handleValue = useMemo(() => {
    if (!handle) return 0;
    return typeof value === "number" ? value : value[handleIndex];
  }, [value, handle, handleIndex]);
  const position = useMemo(() => {
    if (!handleValue) return "0%";
    return `${100 * ((handleValue - min) / (max - min))}%`;
  }, [max, min, handleValue]);
  const handleStep = useCallback((key, shiftKey = false) => {
    const skipStep = typeof shiftStep === "function" ? shiftStep(step) : shiftStep ?? step;
    const increment = shiftKey || PAGE_KEYS.includes(key) ? skipStep : step;
    if (DIRECTION_KEYS[reverse ? "decrease" : "increase"].includes(key)) {
      setValue(handleValue + increment, handleIndex);
    } else if (DIRECTION_KEYS[reverse ? "increase" : "decrease"].includes(key)) {
      setValue(handleValue - increment, handleIndex);
    }
  }, [shiftStep, step, reverse, setValue, handleValue, handleIndex]);
  const handleKeyDown = useCallback((event) => {
    if (!ALL_KEYS.has(event.key)) return;
    if (event.shiftKey && END_KEYS.includes(event.key)) return;
    event.preventDefault();
    if (event.key === "Home") {
      setValue(min, handleIndex, {
        endKey: true
      });
    } else if (event.key === "End") {
      setValue(max, handleIndex, {
        endKey: true
      });
    } else {
      handleStep(event.key, event.shiftKey);
    }
  }, [handleStep, max, min, setValue, handleIndex]);
  useEffect(() => {
    return () => {
      if (handle && handles.has(handle)) {
        handles.delete(handle);
      }
    };
  }, [handle, handles]);
  const ariaValueContent = useMemo(() => {
    if (!ariaValuetext) return String(handleValue);
    return ariaValuetext({
      value,
      index: handleIndex,
      valueNow: handleValue
    });
  }, [ariaValuetext, handleIndex, handleValue, value]);
  return /* @__PURE__ */ jsx("div", { ref: setHandleRef, className: css(sliderHandleStyles), role: "slider", "aria-label": ariaLabel, "aria-disabled": disabled, "aria-valuenow": handleValue, "aria-valuemin": min, "aria-valuemax": max, "aria-valuetext": ariaValueContent, tabIndex: disabled ? -1 : 0, onKeyDown: disabled ? void 0 : handleKeyDown, style: {
    insetInlineStart: position
  }, "data-c11n-component": "Slider.Handle" });
};
const InputTooltip = ({
  toolTipHeader,
  toolTipStatement
}) => /* @__PURE__ */ jsxs(Tooltip.Root, { children: [
  /* @__PURE__ */ jsx(Tooltip.Trigger, {}),
  /* @__PURE__ */ jsx(Tooltip.Portal, { children: /* @__PURE__ */ jsxs(Tooltip.Content, { children: [
    /* @__PURE__ */ jsxs(React.Fragment, { children: [
      /* @__PURE__ */ jsx(Paragraph, { children: toolTipHeader }),
      /* @__PURE__ */ jsx("br", {}),
      /* @__PURE__ */ jsx(Paragraph, { children: toolTipStatement })
    ] }),
    /* @__PURE__ */ jsx(Tooltip.Arrow, {})
  ] }) })
] });
const CustomSlider = ({
  id,
  min,
  max,
  step,
  defaultValue,
  percentageQuestion,
  onChange,
  onAfterChange,
  sliderQuestion,
  toolTipHeader,
  toolTipStatement
}) => {
  const [sliderValue, setSliderValue] = useState(defaultValue);
  useEffect(() => {
    setSliderValue(defaultValue);
  }, [defaultValue]);
  const labelText = percentageQuestion ? `${sliderValue}%` : `$${currencyFormatter(sliderValue, 2)}`;
  const handlePercent = Math.min(97, Math.max(3, (sliderValue - min) / (max - min) * 100));
  const formField = useMemo(() => {
    return /* @__PURE__ */ jsx(FormField, { label: /* @__PURE__ */ jsx(Label, { htmlFor: id, "data-testid": `${id}-label`, children: /* @__PURE__ */ jsxs("span", { className: sliderLabel, children: [
      sliderQuestion,
      toolTipHeader && toolTipStatement && /* @__PURE__ */ jsx(InputTooltip, { toolTipHeader, toolTipStatement })
    ] }) }), control: /* @__PURE__ */ jsxs(Box, { css: gridWithMargin, children: [
      /* @__PURE__ */ jsxs(Box, { css: sliderWrapper, children: [
        /* @__PURE__ */ jsx(Box, { css: sliderValueLabel, style: {
          left: `${handlePercent}%`
        }, children: labelText }),
        /* @__PURE__ */ jsxs(Slider.Root, { id, min, max, value: sliderValue, step, onValueChange: (values) => {
          const next = Array.isArray(values) ? values[0] : values;
          setSliderValue(next);
          onChange(next);
        }, onPointerUp: onAfterChange, onKeyUp: (e) => {
          if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "PageUp", "PageDown"].includes(e.key)) {
            if (onAfterChange) onAfterChange();
          }
        }, children: [
          /* @__PURE__ */ jsx(Slider.Track, { children: /* @__PURE__ */ jsx(Slider.ActiveTrack, {}) }),
          /* @__PURE__ */ jsx(NoTooltipSliderHandle, { ariaLabel: sliderQuestion, ariaValuetext: ({
            valueNow
          }) => percentageQuestion ? `${valueNow}%` : `$${currencyFormatter(valueNow, 2)}` })
        ] })
      ] }),
      /* @__PURE__ */ jsxs(Box, { css: gridWithPadding, children: [
        /* @__PURE__ */ jsx(Box, { children: percentageQuestion ? `${min}%` : `$${min}` }),
        /* @__PURE__ */ jsx(Box, { css: gridEnd, children: percentageQuestion ? `${max}%` : `$${currencyFormatter(max, 2)}` })
      ] })
    ] }) });
  }, [sliderValue, labelText, handlePercent, id, sliderQuestion, toolTipHeader, toolTipStatement, min, max, percentageQuestion, step, onAfterChange, onChange]);
  return /* @__PURE__ */ jsx(Box, { css: formFieldWrapper, children: formField });
};
export {
  CustomSlider as default
};
